
import { Loader, Mail } from 'lucide-react';
import * as React from 'react';

import { VerifyLinkSent } from '@/components/toast-messages';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { useVerifyAccountRequest } from '@/services/auth/mutations';

import {
  AuthTemplate,
  FooterLinks,
} from '@/pages/unauthenticated/auth/components';

interface ExpiredVerifyTokenProps {
  email: string;
  heading: string;
}

export const ExpiredVerifyToken = ({
  email,
  heading,
}: ExpiredVerifyTokenProps) => {
  const { toast } = useToast();

  const { mutate, isPending } = useVerifyAccountRequest({
    onSuccess: () => {
      toast({
        variant: 'success',
        title: 'Email Sent',
        description: <VerifyLinkSent email={email} />,
      });
    },
    onError: () => {
      toast({
        variant: 'destructive',
        title: 'Uh oh! Something went wrong.',
        description: 'There was a problem sending your verification email.',
      });
    },
  });

  return (
    <AuthTemplate
      heading={heading}
      subheading={<ExpiredTokenSubheading />}
      footer={<FooterLinks />}
    >
      <Button
        type="button"
        onClick={() => mutate({ email })}
        disabled={isPending}
        className="w-full"
      >
        {isPending ? (
          <Loader className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <Mail className="w-4 h-4 mr-2" />
        )}
        Resend verification link
      </Button>
    </AuthTemplate>
  );
};

function ExpiredTokenSubheading() {
  return (
    <div className="flex flex-col space-y-2">
      <span>
        Uh oh, this link has <span className="font-semibold">expired</span>.
        Please click the link below to restart the process.
      </span>
      <span>
        If you don&apos;t receive an email in 5 minutes, check your spam or junk
        folder, or click below to resend.
      </span>
    </div>
  );
}
